import { helper, xstate } from '@upflowy/types';
import { v4 as uuidv4 } from 'uuid';
import { assign, StateNode, StateNodeConfig, TransitionConfigOrTarget } from 'xstate';
import { AssignAction, Assigner, TransitionConfig, TransitionsConfigMap } from 'xstate/lib/types';

import { endsWith, mapObject, startsWith } from './utils';

import { IContext, XStateMachineConfig, XStateStateSchema } from '../types';

type StateEvent = xstate.StateEvent;

export const EVENT_TYPE_DISMISS = 'DISMISS';

interface IAction {
  startsWith: string[];
  endsWith: string[];
}

interface IndexedTransitionsConfigMap extends TransitionsConfigMap<IContext, StateEvent> {
  [key: string]: unknown;
}

export const assignContextOnAny = (
  config: XStateMachineConfig,
  doAssign: (assignment: Assigner<IContext, StateEvent>) => AssignAction<IContext, StateEvent>,
  actions: IAction,
): XStateMachineConfig => {
  const enhancedConfig = config;
  Object.keys(enhancedConfig.states ?? {}).forEach((stateName) => {
    const state = enhancedConfig?.states?.[stateName];
    if (!state) return;
    const events = Object.keys(state.on ?? {});
    events.forEach((eventName) => {
      if (startsWith(eventName, actions.startsWith) || endsWith(eventName, actions.endsWith)) {
        if (!state.on) return;
        if (Array.isArray(state.on)) throw Error('Array for on action not supported');
        const stateOn = state.on as IndexedTransitionsConfigMap;
        const target = stateOn?.[eventName];
        stateOn[eventName] = {
          ...(typeof target !== 'object' ? { target } : target),
          actions: doAssign((context: Record<string, unknown>, event) => {
            let contextWithFlowSession = {
              session_id: uuidv4(),
              ...context,
              ...event.data,
            };

            if (typeof contextWithFlowSession.session_id === 'undefined') {
              contextWithFlowSession = {
                ...contextWithFlowSession,
                session_id: uuidv4(),
              };
            }
            return contextWithFlowSession;
          }),
        };
      }
    });
  });
  return enhancedConfig;
};

export function clearContext(enhancedConfig: XStateMachineConfig) {
  Object.keys(enhancedConfig.states ?? {}).forEach((stateName) => {
    if (!enhancedConfig.states) return;
    const state = enhancedConfig.states?.[stateName];
    if (!state) return;
    const events = Object.keys(enhancedConfig.states[stateName]?.on ?? {});
    events.forEach((eventName) => {
      if (eventName.startsWith(EVENT_TYPE_DISMISS)) {
        if (Array.isArray(state.on)) throw Error('Array for on action not supported');
        const stateOn = state.on as IndexedTransitionsConfigMap;
        const target = stateOn?.[eventName];
        if (!stateOn) return;
        stateOn[eventName] = {
          ...((typeof target === 'string' ? { target } : target) as Record<string, unknown>),
          actions: assign((ctx: Record<string, unknown>) => {
            const res: Record<string, unknown> = {};
            Object.keys(ctx).map((i) => {
              res[i] = undefined;
              return res[i];
            });
            return res;
          }),
        };
      }
    });
  });
}

function addActionsToTransition(
  transition: TransitionConfigOrTarget<IContext, StateEvent>,
  action: string,
): TransitionConfigOrTarget<IContext, StateEvent> {
  if (Array.isArray(transition)) {
    return transition.map((t) => addActionsToTransition(t, action)) as TransitionConfig<
      IContext,
      StateEvent
    >;
  }
  if (typeof transition === 'string') return { target: transition, actions: action };
  if (!transition) return { actions: action };
  if (transition instanceof StateNode) throw new Error('Unable to handle StateNode');
  if (typeof transition === 'object') {
    return {
      ...transition,
      actions: [transition.actions, action].flat().filter(helper.isNotUndefined),
    };
  }
  throw new Error('Unable to map transition');
}

function addActionsToState(
  state: StateNodeConfig<IContext, XStateStateSchema, StateEvent>,
  actionMap: Record<string, string>,
) {
  if (Array.isArray(state.on)) throw Error('Array transition config not supported');
  if (!state.on) return state;
  return {
    ...state,
    on: mapObject(state?.on, (transition, key) => {
      if (!actionMap[key]) return transition;
      return addActionsToTransition(
        transition as TransitionConfigOrTarget<IContext, StateEvent>,
        actionMap[key],
      );
    }),
  };
}

export function addActionsToEvents(
  config: XStateMachineConfig,
  actionMap: Record<string, string>,
): XStateMachineConfig {
  return {
    ...config,
    states: {
      ...config.states,
      ...mapObject(config.states, (state) => addActionsToState(state, actionMap)),
    },
  };
}
